import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import MaskedInput from 'react-text-mask';
import PhoneInput from 'react-phone-input-2';
import it from 'react-phone-input-2/lang/it.json';
import fr from 'react-phone-input-2/lang/fr.json';
import { union } from 'lodash';
import { Map } from 'immutable';

const ENTER_KEY_CODE = 13;

const info_tooltip = {
	old_conv_card_number: {
		title: 'Codice tessera',
		'1': 'Inserisci qui il codice della tua tessera relativa alla convenzione. È possibile che venga verificata la validità della stessa e che quindi tu abbia diritto alla convenzione in corso.'
	},
	old_phone: {
		title: 'Telefono',
		'1': 'Inserisci il tuo numero di telefono mobile o fisso. Ci servirà per contattarti in caso di necessità per aiutarci velocizzare il processo di attivazione della tua fornitura!'
	}
};

const FieldString = ({ label, name, content, tooltipTextType }) => {
	const info = info_tooltip[name] ?
		<i key={'icon-' + name}>
			<span>
				<h6>{info_tooltip[name].title}</h6>
				<p>{info_tooltip[name][tooltipTextType]}</p>
			</span>
		</i>
		: '';

	return [
		<label key={'label-' + name} htmlFor={name}>{label}</label>,
		info,
		<div key={'content-' + name}>{content}</div>
	];
};

FieldString.propTypes = {
	label: PropTypes.string,
	name: PropTypes.string,
	content: PropTypes.string,
	tooltipTextType: PropTypes.string
};

const renderFieldText = ({ input, label, focus, blur, tooltipTextType, labelClass = "", iconClass = "", inputClass = "", autocomplete = true, required = false, disabled = false, disablePaste = false, meta: { touched, error, warning } }) => {
	let feedback;
	if (touched) {
		if (error) {
			feedback = <div key={'error-' + input.name} className="invalid-feedback">{error}</div>;
		}
		if (warning) {
			feedback = <div key={'warning-' + input.name} className="invalid-feedback warning">{warning}</div>;
		}
	}

	const info = info_tooltip[input.name] ?
		<i key={'icon-' + input.name} className={iconClass}>
			<span>
				<h6>{info_tooltip[input.name].title}</h6>
				<p>{info_tooltip[input.name][tooltipTextType]}</p>
			</span>
		</i>
		: '';

	autocomplete = autocomplete ? "on" : "new-password";

	return [
		<label key={'label-' + input.name} className={labelClass + (required ? " required" : "")} htmlFor={input.name}>{label}</label>,
		info,
		<input {...input}
			key={'input-' + input.name}
			type="text"
			name={input.name}
			id={input.name}
			className={"form-control " + inputClass + (feedback ? ' is-invalid' : '')}
			autoComplete={autocomplete}
			disabled={disabled}
			onPaste={(e) => {
				if (disablePaste) {
					e.preventDefault();
				}
			}}
			onFocus={(e) => {
				if (focus) {
					focus();
				}
				input.onFocus();
			}}
			onKeyPress={(event) => {
				if (event.charCode === ENTER_KEY_CODE) {
					event.preventDefault();
				}
			}}
			onBlur={() => {
				let value = input.value.trim();
				if (blur) {
					value = blur(value);
				}
				input.onBlur(value);
			}} />,
		feedback
	];
};

renderFieldText.propTypes = {
	input: PropTypes.object,
	label: PropTypes.string,
	focus: PropTypes.func,
	blur: PropTypes.func,
	tooltipTextType: PropTypes.string,
	labelClass: PropTypes.string,
	iconClass: PropTypes.string,
	inputClass: PropTypes.string,
	autocomplete: PropTypes.bool,
	required: PropTypes.bool,
	disabled: PropTypes.bool,
	disablePaste: PropTypes.bool,
	meta: PropTypes.object
};

const renderFieldMask = ({ input, label, handleChangeMaskField, tooltipTextType, labelClass = "", inputClass = "", required = false, disabled = false, meta: { touched, error, warning } }) => {
	let feedback;
	if (touched) {
		if (error) {
			feedback = <div key={'error-' + input.name} className="invalid-feedback">{error}</div>;
		}
		if (warning) {
			feedback = <div key={'warning-' + input.name} className="invalid-feedback warning">{warning}</div>;
		}
	}

	const info = info_tooltip[input.name] ?
		<i key={'icon-' + input.name}>
			<span>
				<h6>{info_tooltip[input.name].title}</h6>
				<p>{info_tooltip[input.name][tooltipTextType]}</p>
			</span>
		</i>
		: '';

	return [
		<label key={'label-' + input.name} className={labelClass + (required ? " required" : "")} htmlFor={input.name}>{label}</label>,
		info,
		<MaskedInput
			key={'input-' + input.name}
			name={input.name}
			id={input.name}
			className={"form-control " + inputClass + (feedback ? ' is-invalid' : '')}
			mask={[/\d/, /\d/, /\d/, /\d/, /\d/, /\d/]}
			placeholder="------"
			placeholderChar="-"
			autoComplete="off"
			disabled={disabled}
			guide={true}
			onBlur={() => {
				input.onBlur(input.value.trim());
			}}
			onChange={(e) => {
				handleChangeMaskField(e);
			}}
		/>,
		feedback
	];
};

renderFieldMask.propTypes = {
	input: PropTypes.object,
	label: PropTypes.string,
	handleChangeMaskField: PropTypes.func,
	tooltipTextType: PropTypes.string,
	labelClass: PropTypes.string,
	inputClass: PropTypes.string,
	required: PropTypes.bool,
	disabled: PropTypes.bool,
	meta: PropTypes.object
};

const SELECT_MAX_RESULT = 20;
const SELECT_DELAY_RESULT = 300;

const filterSelectOptions = (input = '', callback, options) => {
	input = input.toLowerCase();

	// search for options that "start with *"
	const start_options = options.filter(opt => {
		return opt.label.toLowerCase().substr(0, input.length) === input;
	});

	// search for options that "includes *"
	const include_options = options.filter(opt => {
		return opt.label.toLowerCase().includes(input);
	});

	// merge results, first "start with *", then "includes *"
	options = union(start_options, include_options);

	// IDEA: test for highlight matched words, IT DOSEN'T WORK!
	// options = options.map(opt => {
	// 	const ind = opt.label.toLowerCase().indexOf(input);
	// 	if(ind > -1) {
	// 		opt.label = opt.label.substr(0, ind) + '<b>' + opt.label.substr(ind, input.length) + '</b>' + opt.label.substr(input.length);
	// 	}
	// 	return opt;
	// });

	const data = {
		options: options.slice(0, SELECT_MAX_RESULT),
		complete: options.length <= SELECT_MAX_RESULT,
	};
	setTimeout(function () {
		callback(null, data);
	}, SELECT_DELAY_RESULT);
};

const renderFieldSelect = ({ input, options, label, change, tooltipTextType, searchable = false, required = false, meta: { touched, error, warning } }) => {
	let feedback;
	if (touched) {
		if (error) {
			feedback = <div key={'error-' + input.name} className="invalid-feedback">{error}</div>;
		}
		if (warning) {
			feedback = <div key={'warning-' + input.name} className="invalid-feedback warning">{warning}</div>;
		}
	}

	const info = info_tooltip[input.name] ?
		<i key={'icon-' + input.name}>
			<span>
				<h6>{info_tooltip[input.name].title}</h6>
				<p>{info_tooltip[input.name][tooltipTextType]}</p>
			</span>
		</i>
		: '';

	const SelectComponent = searchable ? Select.Async : Select;
	const loadOptions = searchable ? (input, callback) => filterSelectOptions(input, callback, options) : false;
	const allOptions = searchable ? [] : options;

	// NOTE: se da API -> Map, se da onChange -> Object
	const preselectValue = (input.value) ? Map.isMap(input.value) ? input.value.toJS() : input.value : "";

	return [
		<label key={'label-' + input.name} className={required ? "required" : ""} htmlFor={input.name}>{label}</label>,
		info,
		<SelectComponent
			{...input}
			key={'input-' + input.name}
			type="text"
			value={preselectValue}
			cacheOptions={true}
			loadOptions={loadOptions}
			defaultOptions
			options={allOptions}
			searchable={searchable}
			placeholder=""
			noResultsText="Nessun risultato trovato"
			searchPromptText="Scrivi per iniziare la ricerca..."
			loadingPlaceholder="Ricerca in corso..."
			onChange={(selectedOption) => {
				if (change) {
					change(selectedOption);
				}
				input.onChange(selectedOption);
			}}
			onBlur={() => {
				input.onBlur(input.value);
			}}
			className={"form-control" + (feedback ? ' is-invalid' : '')}
		/>,
		feedback
	];
};

renderFieldSelect.propTypes = {
	input: PropTypes.object,
	options: PropTypes.array,
	label: PropTypes.string,
	change: PropTypes.func,
	tooltipTextType: PropTypes.string,
	searchable: PropTypes.bool,
	required: PropTypes.bool,
	meta: PropTypes.object
};

const renderFieldRadio = ({ input, options, label, className, optionsContainerClassName, labelOptionClassName, centered, inverted, separator, forceInline, tooltipTextType, required, meta: { touched, error, warning } }) => {
	let feedback;
	if (touched) {
		if (error) {
			feedback = <div key={'error-' + input.name} className="invalid-feedback">{error}</div>;
		}
		if (warning) {
			feedback = <div key={'warning-' + input.name} className="invalid-feedback warning">{warning}</div>;
		}
	}

	const info = info_tooltip[input.name] ?
		<i key={'icon-' + input.name} className="right">
			<span>
				<h6>{info_tooltip[input.name].title}</h6>
				<p>{info_tooltip[input.name][tooltipTextType]}</p>
			</span>
		</i>
		: '';

	const opt = options.map((o, i) => {
		return <div className={"form-check form-check-inline" + (optionsContainerClassName ? ' ' + optionsContainerClassName : '')} key={input.name + '_' + i}>
			<input {...input} type="radio" name={input.name} id={input.name + '_' + i} className="styled-checkbox" value={o.value} checked={o.value === input.value} />
			<label className={"form-check-label no-icon" + (labelOptionClassName ? ' ' + labelOptionClassName : '')} htmlFor={input.name + '_' + i}>{o.label}</label>
			{separator && <div className='separator'></div>}
		</div>;
	});

	return [
		<label key={'label-' + input.name} className={required ? "required" : ""} htmlFor={input.name}>{label}</label>,
		info,
		<div key={'radio-' + input.name} className={"form-group-check form-control" + (feedback ? ' is-invalid' : '') + (forceInline ? ' force-inline' : '') + (centered ? ' force-center' : '') + (inverted ? ' inverted' : '') + (className ? ' ' + className : '')}>
			{opt}
		</div>,
		feedback
	];
};

renderFieldRadio.propTypes = {
	input: PropTypes.object,
	options: PropTypes.array,
	label: PropTypes.string,
	className: PropTypes.string,
	optionsContainerClassName: PropTypes.string,
	labelOptionClassName: PropTypes.string,
	centered: PropTypes.bool,
	inverted: PropTypes.bool,
	separator: PropTypes.bool,
	forceInline: PropTypes.bool,
	tooltipTextType: PropTypes.string,
	required: PropTypes.bool,
	meta: PropTypes.object
};


const renderFieldRadioIcon = ({ input, options, change, inverted, meta: { touched, error, warning } }) => {
	let feedback;
	if (touched) {
		if (error) {
			feedback = <div key={'error-' + input.name} className="invalid-feedback">{error}</div>;
		}
		if (warning) {
			feedback = <div key={'warning-' + input.name} className="invalid-feedback warning">{warning}</div>;
		}
	}

	const opt = options.map((o, i) => {
		return <div className="form-check form-check-inline" key={input.name + '_' + i}>
			<input
				{...input}
				type="radio"
				name={input.name}
				id={input.name + '_' + i}
				className={"styled-checkbox icon " + o.icon}
				value={o.value}
				checked={o.value === input.value}
				disabled={o.disabled}
				onChange={(e) => {
					if (change) {
						change(e.target.checked, o.value);
					}
					input.onChange(o.value);
				}}
			/>
			<label className={"form-check-label" + (inverted ? " inverted" : "") + (o.value === input.value ? " selected" : "")} htmlFor={input.name + '_' + i} />
			{o.label && <div className="form-check-info">{o.label}</div>}
		</div>;
	});

	return [
		<div key={'radio-icon-' + input.name} className={"form-group-check icon form-control" + (feedback ? ' is-invalid' : '')}>
			{opt}
		</div>,
		feedback
	];
};

renderFieldRadioIcon.propTypes = {
	input: PropTypes.object,
	options: PropTypes.array,
	change: PropTypes.func,
	inverted: PropTypes.bool,
	meta: PropTypes.object
};

const renderFieldRadioList = ({ input, options, inverted, meta: { touched, error, warning } }) => {
	let feedback;
	if (touched) {
		if (error) {
			feedback = <div key={'error-' + input.name} className="invalid-feedback">{error}</div>;
		}
		if (warning) {
			feedback = <div key={'warning-' + input.name} className="invalid-feedback warning">{warning}</div>;
		}
	}

	const opt = options.map((o, i) => {
		return <div className="form-check form-check-inline" key={input.name + '_' + i}>
			<input {...input} type="radio" name={input.name} id={input.name + '_' + i} className={"styled-checkbox list"} value={o.value} checked={o.value.toLowerCase() === input.value.toLowerCase()} disabled={o.disabled} />
			<label className={"form-check-label" + (inverted ? " inverted" : "")} htmlFor={input.name + '_' + i}>
				<div className='form-check-flag'></div>
				{o.text && <div className="form-check-title">{o.text}</div>}
				{o.description && <div className="form-check-text">{o.description}</div>}
				{o.promo && <div className="form-check-promo">{o.promo}</div>}
			</label>
		</div>;
	});

	return [
		<div key={'radio-list-' + input.name} className={"form-group-check list form-control" + (feedback ? ' is-invalid' : '')}>
			{opt}
		</div>,
		feedback
	];
};

renderFieldRadioList.propTypes = {
	input: PropTypes.object,
	options: PropTypes.array,
	inverted: PropTypes.bool,
	meta: PropTypes.object
};


const renderFieldCheckbox = ({ input, label, centered, forceInline, subLabel, postLabel, meta: { touched, error, warning } }) => {
	let feedback;
	if (touched) {
		if (error) {
			feedback = <div key={'error-' + input.name} className="invalid-feedback">{error}</div>;
		}
		if (warning) {
			feedback = <div key={'warning-' + input.name} className="invalid-feedback warning">{warning}</div>;
		}
	}

	return [
		<div key={'checkbox-' + input.name} className={"form-group-check form-control" + (feedback ? ' is-invalid' : '') + (forceInline ? ' force-inline' : '') + (centered ? ' force-center' : '')}>
			<div className="form-check form-check-inline" key={input.name}>
				<input {...input} type="checkbox" name={input.name} id={input.name} className="styled-checkbox" checked={input.value} />
				<label className={"form-check-label no-icon" + (subLabel ? " label-lg" : "")} htmlFor={input.name}>
					{label}
				</label>
			</div>
		</div>,
		<div key={'subLabel' + input.name}>
			{subLabel}
		</div>,
		<div key={'postLabel' + input.name}>
			{postLabel}
		</div>,
		feedback
	];
};

renderFieldCheckbox.propTypes = {
	input: PropTypes.object,
	label: PropTypes.string,
	centered: PropTypes.bool,
	forceInline: PropTypes.bool,
	subLabel: PropTypes.object,
	postLabel: PropTypes.object,
	meta: PropTypes.object
};

const renderFieldCheckboxList = ({ input, options, change, inverted, higher, meta: { touched, error, warning } }) => {
	let feedback;
	if (touched) {
		if (error) {
			feedback = <div key={'error-' + input.name} className="invalid-feedback">{error}</div>;
		}
		if (warning) {
			feedback = <div key={'warning-' + input.name} className="invalid-feedback warning">{warning}</div>;
		}
	}
	const opt = options.map((o, i) => {
		return <div className="form-check form-check-inline" key={input.name + '_' + i}>
			<input
				{...input}
				type="checkbox"
				name={input.name + '_' + i}
				id={input.name + '_' + i}
				className={"styled-checkbox list" + (o.included ? " included" : "")}
				checked={input.value.indexOf(o.value) !== -1}
				disabled={o.disabled || o.included}
				onChange={(e, checked) => {
					const newValue = [...input.value];
					if (e.target.checked) {
						newValue.push(o.value);
					} else {
						newValue.splice(newValue.indexOf(o.value), 1);
					}
					if (change) {
						change(e.target.checked, o.value);
					}
					input.onChange(newValue);
				}}
				onBlur={(e) => {
					input.onBlur(input.value);
				}} />
			<label className={"form-check-label" + (inverted ? " inverted" : "") + (higher ? " higher" : "")} htmlFor={input.name + '_' + i}>
				<div className='form-check-flag'></div>
				{o.text && <div className="form-check-title">
					{o.text}
				</div>}
				{o.description && <div className="form-check-text">{o.description}</div>}
				{o.promo && <div className="form-check-promo">{o.promo}</div>}
				{o.link && <a className="form-check-link" href={o.link} target='_blank' rel='noopener noreferrer'><span></span></a>}
			</label>
		</div>;
	});

	return [
		<div key={'radio-list-' + input.name} className={"form-group-check list form-control" + (feedback ? ' is-invalid' : '')}>
			{opt}
		</div>,
		feedback
	];
};

renderFieldCheckboxList.propTypes = {
	input: PropTypes.object,
	options: PropTypes.array,
	change: PropTypes.func,
	inverted: PropTypes.bool,
	higher: PropTypes.bool,
	meta: PropTypes.object
};


// ORIGINAL SELECT WITH DEFAULT BROWSER BEHAVIOR
// const renderFieldSelectBase = ({ input, options, label, meta: { touched, error, warning } }) => {
// 	let feedback;
// 	if(touched) {
// 		if(error) {
// 			feedback = <div className="invalid-feedback">{error}</div>;
// 		}
// 		if(warning) {
// 			feedback = <div className="invalid-feedback warning">{warning}</div>;
// 		}
// 	}
//
// 	const info = info_tooltip[input.name] ?
// 	<i>
// 		<span>
// 			<h6>{info_tooltip[input.name].title}</h6>
// 			<p>{info_tooltip[input.name].text}</p>
// 		</span>
// 	</i>
// 	: '';
//
// 	const opt = options.map((o, i) => {
// 		// console.log(o.value, input.value, o.value === input.value);
// 		return <option value={o.value} key={input.name + '_' + i}>
// 			{o.label}
// 		</option>;
// 	});
//
// 	return <div>
// 		<label htmlFor={input.name}>{label}</label>
// 		{info}
// 		<select {...input} className="custom-select">
// 			<option value=""></option>
// 			{opt}
// 		</select>
// 		{feedback}
// 	</div>;
// };
//
// renderFieldSelectBase.propTypes = {
// 	input: PropTypes.object,
// 	options: PropTypes.array,
// 	label: PropTypes.string,
// 	meta: PropTypes.object
// };

const renderFieldDatePicker = ({ input, label, focus, minDate = null, maxDate = null, tooltipTextType, labelClass = "", inputClass = "", required = false, meta: { touched, error, warning } }) => {
	let feedback;
	if (touched) {
		if (error) {
			feedback = <div key={'error-' + input.name} className="extern-component invalid-feedback">{error}</div>;
		}
		if (warning) {
			feedback = <div key={'warning-' + input.name} className="extern-component invalid-feedback warning">{warning}</div>;
		}
	}

	const info = info_tooltip[input.name] ?
		<i key={'icon-' + input.name}>
			<span>
				<h6>{info_tooltip[input.name].title}</h6>
				<p>{info_tooltip[input.name][tooltipTextType]}</p>
			</span>
		</i>
		: '';

	return [
		<label key={'label-' + input.name} className={labelClass + (required ? " required" : "")} htmlFor={input.name}>{label}</label>,
		info,
		<DatePicker
			key={'input-' + input.name}
			calendarClassName="calendar-container"
			className={"form-control " + inputClass + (feedback ? ' is-invalid' : '')}
			dateFormat="dd/MM/yyyy"
			dropdownMode="select"
			minDate={minDate}
			maxDate={maxDate}
			name={input.name}
			peekNextMonth
			placeholderText="DD/MM/YYYY"
			selected={input.value ? input.value : null}
			showMonthDropdown
			showYearDropdown
			autoComplete="off"
			onBlur={() => {
				input.onBlur(input.value);
			}}
			onChange={(value) => {
				input.onChange(value);
			}}
			onFocus={() => {
				if (focus) {
					focus();
				}
				input.onFocus();
			}}
		/>,
		feedback
	];
};

renderFieldDatePicker.propTypes = {
	input: PropTypes.object,
	label: PropTypes.string,
	focus: PropTypes.func,
	tooltipTextType: PropTypes.string,
	labelClass: PropTypes.string,
	inputClass: PropTypes.string,
	required: PropTypes.bool,
	meta: PropTypes.object
};

// focus, autocomplete
const renderFieldPhone = ({ input, label, inputClass = '', labelClass = '', placeholder = '', required = false, disabled = false, change = null, blur = null, i18n, meta: { touched, error, warning } }) => {
	let feedback;
	if (touched) {
		if (error) {
			feedback = <div key={'error-' + input.name} className="invalid-feedback">{error}</div>;
		}
		if (warning) {
			feedback = <div key={'warning-' + input.name} className="invalid-feedback warning">{warning}</div>;
		}
	}

	let localization = it;
	const preferredCountries = ['it'];
	const country = 'it';
	switch (i18n.language) {
		case 'en':
			localization = {};
			// preferredCountries = [ 'us', 'gb' ];
			// country = 'us';
			break;
		case 'fr':
			localization = fr;
			// preferredCountries = [ 'fr' ];
			// country = 'fr';
			break;
		default:
			break;
	}

	return [
		<label key={'label-' + input.name} className={labelClass + (required ? " required" : "")} htmlFor={input.name}>{label}</label>,
		<PhoneInput
			key={'input-' + input.name}
			{...input}
			country={country}
			value={input.value}
			placeholder={placeholder}
			preferredCountries={preferredCountries}
			localization={localization}
			specialLabel=''
			disabled={disabled}
			// enableSearch
			// disableSearchIcon
			// searchPlaceholder
			// searchNotFound
			containerClass={'text-prefix' + (feedback ? ' is-invalid' : '')}
			inputClass={inputClass + (feedback ? ' is-invalid' : '')}
			buttonClass='form-prefix'
			dropdownClass='list'
			onChange={(value, country /*, e, formattedValue */) => {
				const prefix = `+${country.dialCode}`;
				change && change(prefix);
				input.onChange(value);
			}}
			onBlur={(e, country) => {
				input.onBlur(input.value);
			}}
		//* NOTA: onBlur e onFocus hanno come parametri: e, country
		//*   e è l'evento
		//*   country è l'oggetto (vedi onChange)
		/>,
		feedback
	];
};

renderFieldPhone.propTypes = {
	input: PropTypes.object,
	label: PropTypes.string,
	inputClass: PropTypes.string,
	labelClass: PropTypes.string,
	placeholder: PropTypes.string,
	required: PropTypes.bool,
	disabled: PropTypes.bool,
	change: PropTypes.func,
	blur: PropTypes.func,
	i18n: PropTypes.object,
	meta: PropTypes.object
};

export { FieldString, renderFieldText, renderFieldMask, renderFieldSelect, renderFieldRadio, renderFieldRadioIcon, renderFieldRadioList, renderFieldCheckbox, renderFieldCheckboxList, renderFieldDatePicker, renderFieldPhone };
